import { applySnapshot, types } from "mobx-state-tree"
import { DictionaryLink } from "modules/common/models/dictionary-link"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { PrintAnchorInfoList, initialState as emptyAnchors } from "modules/common/models/print-anchor"
import { SectionCollapser } from "modules/common/models/section-collapser"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"
import { MimeTypes } from "modules/common/services/files"
import { formatPhoneNumber } from "modules/common/services/formatting/phone"
import { nameof } from "modules/common/services/typescript"
import { texts } from "modules/common/texts"
import { InvoiceFile } from "modules/orders-manage/models/order-file"
import {
  EmployerDictionary,
  initialState as employerState,
} from "modules/spending/employee/models/employee-dictionary"

export const ACCEPT = MimeTypes.apk

/**
 * Поведение планового табеля со временем. Значения совпадают с `WorkloadPlanModes` на бэке.
 */
export const WorkloadPlanModes = {
  /** Табель влияет на план: невыполненное переезжает вправо */
  timesheetDriven: "TimesheetDriven",
  /** Каждый день новый план: график целиком уезжает вправо на прошедшие дни */
  dailyShift: "DailyShift",
  /** Фиксированный план: дни просто уходят в прошлое */
  fixed: "Fixed",
}

const BonusSalaryPercent = types
  .model({ guid: types.string, yearsCount1: types.number, yearsCount2: types.number, percentValue: types.number })
  .named("BonusSalaryPercent")

/**
 * Сеть, из которой вход в систему подтверждается автоматически:
 * IP-адрес (192.168.0.1) либо подсеть (192.168.0.0/24)
 */
const AutomaticNetwork = types
  .model({ address: types.string, comment: types.string })
  .named("AutomaticNetwork")

/**
 * Уведомления о сроках одного вида. Значения совпадают с `*ReminderSettings` на бэке:
 * `enabled` — напоминание заранее, остальное — что ещё слать и во сколько
 */
const QuestionReminderSettings = types
  .model({
    enabled: types.boolean,
    daysBefore: types.array(types.number),
    workingDays: types.boolean,
    sendTime: types.string,
    onDeadlineDay: types.boolean,
    overdue: types.boolean,
    overdueDays: types.number,
    noImplementer: types.boolean,
  })
  .named("QuestionReminderSettings")

const MailReminderSettings = types
  .model({
    enabled: types.boolean,
    daysBefore: types.array(types.number),
    workingDays: types.boolean,
    sendTime: types.string,
    onDeadlineDay: types.boolean,
    overdue: types.boolean,
    overdueDays: types.number,
  })
  .named("MailReminderSettings")

const DeadlineReminders = types
  .model({
    questions: QuestionReminderSettings,
    mails: MailReminderSettings,
  })
  .named("DeadlineReminders")

const ApplicationSettings = types
  .model({
    productionBudget: types.number,
    productionBudgetDelta: types.number,
    productionQuality: types.number,
    engineerBudget: types.number,
    engineerBudgetDelta: types.number,
    engineerQuality: types.number,
    engineerQualityDelta: types.number,
    engineerTime: types.number,
    engineerTimeDelta: types.number,
    address: types.string,
    directorName: types.string,
    companyName: types.string,
    shortCompanyName: types.string,
    okved: types.string,
    phoneNumber: types.string,
    email: types.string,
    emailPassword: types.string,
    emailServer: types.string,
    emailPort: types.number,
    emailReadServer: types.string,
    emailReadPort: types.number,
    isSsl: types.boolean,
    sendEmail: types.string,
    directorNameGenitive: types.string,
    administratorPositions: types.string,
    automaticNetworks: types.array(AutomaticNetwork),
    showMinutes: types.boolean,
    prevDaysCount: types.number,
    futureDaysCount: types.number,
    excludeEmployersBonus: types.array(DictionaryLink),
    bonusSalaryPercents: types.array(BonusSalaryPercent),
    roundStep: types.number,
    fieldsToRound: types.array(types.string),
    appVersion: types.string,
    appFile: types.maybeNull(InvoiceFile),
    oneCUrl: types.string,
    oneCLogin: types.string,
    oneCPassword: types.string,
    oneCEnabled: types.boolean,
    overheadDistributionEnabled: types.boolean,
    questionsBoardEnabled: types.boolean,
    questionsCustomColumnsEnabled: types.boolean,
    questionsPortfoliosEnabled: types.boolean,
    questionsPortfolioAuthors: types.array(types.string),
    workloadPlanMode: types.string,
    overheadFixedShare: types.number,
    overheadTaxShare: types.number,
    overheadPaymentShare: types.number,
    deadlineReminders: DeadlineReminders,
  })
  .views((self) => ({
    get formattedPhone() {
      return formatPhoneNumber(self.phoneNumber)
    },
  }))
  .named("ApplicationSettings")

export const SettingsStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      model: ApplicationSettings,
      employers: EmployerDictionary,
      collapser: SectionCollapser,
      techTaskAnchors: PrintAnchorInfoList,
      contentTaskAnchors: PrintAnchorInfoList,
      wopiToken: types.string,
      editorUrl: types.string,
    })
  )
  .actions((self) => ({
    load: flow(function* () {
      try {
        yield self.contentTaskAnchors.load()
        yield self.techTaskAnchors.load()
        yield self.employers.load()
        const snapshot = yield self.transport.get<any>(apiUrls.settings.get)

        applySnapshot(self.model, snapshot)
        self.wopiToken = snapshot.wopiToken
        self.editorUrl = snapshot.editorUrl
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    loadShortSettings: flow(function* () {
      try {
        const snapshot = yield self.transport.get<any>(apiUrls.settings.shortSettings)

        applySnapshot(self.model, snapshot)
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    save: flow(function* (model: ApplicationSettingsSnapshotType) {
      try {
        const snapshot = yield self.transport.post<any>(apiUrls.settings.update, model)

        applySnapshot(self.model, snapshot)
        self.wopiToken = snapshot.wopiToken
        self.editorUrl = snapshot.editorUrl

        self.notify.success(texts.messages.saved)

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    /**
     * Сети автоматического входа правятся в своём окне и уходят на сервер сразу,
     * не дожидаясь сохранения всей формы настроек
     */
    saveAutomaticNetworks: flow(function* (networks: AutomaticNetworkSnapshotType[]) {
      try {
        const saved = yield self.transport.post<any>(apiUrls.settings.networks, { networks })

        self.notify.success(texts.messages.saved)

        return saved as AutomaticNetworkSnapshotType[]
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),

    testOneCConnection: flow(function* (oneCUrl: string, oneCLogin: string, oneCPassword: string) {
      try {
        const model = { oneCUrl, oneCLogin, oneCPassword }
        const data = yield self.transport.post<any>(apiUrls.settings.test1c, model)

        if (data && data.success) {
          self.notify.success(data.message || "Подключение к 1С успешно установлено.")
          return true
        }

        self.notify.error((data && data.message) || "Не удалось подключиться к 1С.")
        return false
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    sendMailTest: flow(function* (emailTo: string, topic: string, message: string) {
      try {
        const model = {
          topic,
          message,
          emailTo,
          isTest: true,
        }
        const data = yield self.transport.post<any>(apiUrls.settings.testEmail, model)
        if (data) {
          self.notify.success("Письмо отправлено!")
        }

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    replaceTemplate: flow(function* (id: string, fileId: string) {
      try {
        yield self.transport.put<any>(apiUrls.templates.technicalTask.contents(id), {
          fileId,
        })

        self.notify.success(texts.messages.saved)

        return true
      } catch (er) {
        self.notify.error(er)

        return false
      }
    }),

    upload: flow(function* (file: File) {
      try {
        const model = new FormData()

        model.append("file", file)
        model.append("accept", ACCEPT)

        const result: UploadFileResult = yield self.transport.post<any>(apiUrls.application.files.upload, model)
        const { id, previewMimeType, mimeType } = result

        const fileBase: FileBase = { fileId: id, fileName: file.name, previewMimeType, mimeType }
        return fileBase
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),
  }))

export const initialState = (): SettingsStoreSnapshotType => ({
  model: {
    engineerBudget: 0,
    engineerBudgetDelta: 0,
    engineerQuality: 0,
    engineerQualityDelta: 0,
    engineerTime: 0,
    engineerTimeDelta: 0,
    productionBudget: 0,
    productionBudgetDelta: 0,
    productionQuality: 0,
    address: "",
    directorName: "",
    companyName: "",
    shortCompanyName: "",
    okved: "",
    phoneNumber: "",
    email: "",
    sendEmail: "",
    emailPassword: "",
    emailServer: "",
    emailPort: 0,
    emailReadServer: "",
    isSsl: false,
    emailReadPort: 0,
    directorNameGenitive: "",
    administratorPositions: "",
    automaticNetworks: [],
    showMinutes: false,
    prevDaysCount: 3,
    futureDaysCount: 0,
    bonusSalaryPercents: [],
    excludeEmployersBonus: [],
    roundStep: 0,
    fieldsToRound: [],
    appVersion: "",
    appFile: null,
    oneCUrl: "",
    oneCLogin: "",
    oneCPassword: "",
    oneCEnabled: false,
    overheadDistributionEnabled: true,
    questionsBoardEnabled: true,
    questionsCustomColumnsEnabled: false,
    questionsPortfoliosEnabled: false,
    questionsPortfolioAuthors: [],
    workloadPlanMode: WorkloadPlanModes.timesheetDriven,
    overheadFixedShare: 18,
    overheadTaxShare: 3,
    overheadPaymentShare: 15,
    deadlineReminders: {
      questions: {
        enabled: true,
        daysBefore: [1],
        workingDays: true,
        sendTime: "10:00",
        onDeadlineDay: true,
        overdue: true,
        overdueDays: 7,
        noImplementer: true,
      },
      mails: {
        enabled: true,
        daysBefore: [1],
        workingDays: true,
        sendTime: "10:00",
        onDeadlineDay: true,
        overdue: true,
        overdueDays: 7,
      },
    },
  },
  employers: employerState(),
  collapser: {
    opened: {
      kpi: false,
      connection1c: false,
      overhead: false,
      general: false,
    },
  },
  contentTaskAnchors: emptyAnchors(apiUrls.orders.contentTasks.anchors),
  techTaskAnchors: emptyAnchors(apiUrls.orders.summary.tasks.anchors),
  editorUrl: "",
  wopiToken: "",
})

export const fields = {
  productionBudget: nameof((a: ApplicationSettingsSnapshotType) => a.productionBudget) as string,
  productionBudgetDelta: nameof((a: ApplicationSettingsSnapshotType) => a.productionBudgetDelta) as string,
  productionQuality: nameof((a: ApplicationSettingsSnapshotType) => a.productionQuality) as string,
  engineerBudget: nameof((a: ApplicationSettingsSnapshotType) => a.engineerBudget) as string,
  engineerBudgetDelta: nameof((a: ApplicationSettingsSnapshotType) => a.engineerBudgetDelta) as string,
  engineerQuality: nameof((a: ApplicationSettingsSnapshotType) => a.engineerQuality) as string,
  engineerQualityDelta: nameof((a: ApplicationSettingsSnapshotType) => a.engineerQualityDelta) as string,
  engineerTime: nameof((a: ApplicationSettingsSnapshotType) => a.engineerTime) as string,
  engineerTimeDelta: nameof((a: ApplicationSettingsSnapshotType) => a.engineerTimeDelta) as string,
  address: nameof((a: ApplicationSettingsSnapshotType) => a.address) as string,
  directorName: nameof((a: ApplicationSettingsSnapshotType) => a.directorName) as string,
  directorNameGenitive: nameof((a: ApplicationSettingsSnapshotType) => a.directorNameGenitive) as string,
  companyName: nameof((a: ApplicationSettingsSnapshotType) => a.companyName) as string,
  shortCompanyName: nameof((a: ApplicationSettingsSnapshotType) => a.shortCompanyName) as string,
  okved: nameof((a: ApplicationSettingsSnapshotType) => a.okved) as string,
  phoneNumber: nameof((a: ApplicationSettingsSnapshotType) => a.phoneNumber) as string,
  email: nameof((a: ApplicationSettingsSnapshotType) => a.email) as string,
  emailPassword: nameof((a: ApplicationSettingsSnapshotType) => a.emailPassword) as string,
  emailServer: nameof((a: ApplicationSettingsSnapshotType) => a.emailServer) as string,
  emailPort: nameof((a: ApplicationSettingsSnapshotType) => a.emailPort) as string,
  emailReadServer: nameof((a: ApplicationSettingsSnapshotType) => a.emailReadServer) as string,
  emailReadPort: nameof((a: ApplicationSettingsSnapshotType) => a.emailReadPort) as string,
  isSsl: nameof((a: ApplicationSettingsSnapshotType) => a.isSsl) as string,
  sendEmail: nameof((a: ApplicationSettingsSnapshotType) => a.sendEmail) as string,
  administratorPositions: nameof((a: ApplicationSettingsSnapshotType) => a.administratorPositions) as string,
  automaticNetworks: nameof((a: ApplicationSettingsSnapshotType) => a.automaticNetworks) as string,
  showMinutes: nameof((a: ApplicationSettingsSnapshotType) => a.showMinutes) as string,
  prevDaysCount: nameof((a: ApplicationSettingsSnapshotType) => a.prevDaysCount) as string,
  futureDaysCount: nameof((a: ApplicationSettingsSnapshotType) => a.futureDaysCount) as string,
  bonusSalaryPercents: nameof((a: ApplicationSettingsSnapshotType) => a.bonusSalaryPercents) as string,
  excludeEmployersBonus: nameof((a: ApplicationSettingsSnapshotType) => a.excludeEmployersBonus) as string,
  roundStep: nameof((a: ApplicationSettingsSnapshotType) => a.roundStep) as string,
  fieldsToRound: nameof((a: ApplicationSettingsSnapshotType) => a.fieldsToRound) as string,
  appVersion: nameof((a: ApplicationSettingsSnapshotType) => a.appVersion) as string,
  appFile: nameof((a: ApplicationSettingsSnapshotType) => a.appFile) as string,
  oneCUrl: nameof((a: ApplicationSettingsSnapshotType) => a.oneCUrl) as string,
  oneCLogin: nameof((a: ApplicationSettingsSnapshotType) => a.oneCLogin) as string,
  oneCPassword: nameof((a: ApplicationSettingsSnapshotType) => a.oneCPassword) as string,
  oneCEnabled: nameof((a: ApplicationSettingsSnapshotType) => a.oneCEnabled) as string,
  overheadDistributionEnabled: nameof(
    (a: ApplicationSettingsSnapshotType) => a.overheadDistributionEnabled
  ) as string,
  workloadPlanMode: nameof((a: ApplicationSettingsSnapshotType) => a.workloadPlanMode) as string,
  questionsBoardEnabled: nameof(
    (a: ApplicationSettingsSnapshotType) => a.questionsBoardEnabled
  ) as string,
  questionsCustomColumnsEnabled: nameof(
    (a: ApplicationSettingsSnapshotType) => a.questionsCustomColumnsEnabled
  ) as string,
  questionsPortfoliosEnabled: nameof(
    (a: ApplicationSettingsSnapshotType) => a.questionsPortfoliosEnabled
  ) as string,
  questionsPortfolioAuthors: nameof(
    (a: ApplicationSettingsSnapshotType) => a.questionsPortfolioAuthors
  ) as string,
  overheadFixedShare: nameof((a: ApplicationSettingsSnapshotType) => a.overheadFixedShare) as string,
  overheadTaxShare: nameof((a: ApplicationSettingsSnapshotType) => a.overheadTaxShare) as string,
  overheadPaymentShare: nameof((a: ApplicationSettingsSnapshotType) => a.overheadPaymentShare) as string,
  deadlineReminders: nameof((a: ApplicationSettingsSnapshotType) => a.deadlineReminders) as string,
}

export type SettingsStoreType = typeof SettingsStore.Type
export type SettingsStoreSnapshotType = typeof SettingsStore.SnapshotType
export type ApplicationSettingsSnapshotType = typeof ApplicationSettings.SnapshotType
export type ApplicationSettingsType = typeof ApplicationSettings.Type

export type BonusSalaryPercentType = typeof BonusSalaryPercent.Type

export type AutomaticNetworkType = typeof AutomaticNetwork.Type
export type AutomaticNetworkSnapshotType = typeof AutomaticNetwork.SnapshotType

export type QuestionReminderSettingsSnapshotType = typeof QuestionReminderSettings.SnapshotType
export type MailReminderSettingsSnapshotType = typeof MailReminderSettings.SnapshotType
